import { z } from "zod";
import { ArtistIntakeSchema, LocalArtistIntakeSchema, DarkRoomReviewSchema } from "./schemas";
import { runDarkRoomLiteMock, type ShadowArtistRecord } from "./darkRoom";
import { consentSensitivity, requireAnalysisConsent } from "./consent";
import type { AiRouteDecision } from "./aiRouter";
import type { ShadowRepository } from "./persistence";

const recordId = z.string().min(1).max(200).regex(/^[A-Za-z0-9_-]+$/);
export const RunPersistenceSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("local_only"), target: z.literal("memory") }).strict(),
  z.object({ state: z.literal("unavailable"), target: z.literal("server"), warning: z.literal("persistence_disabled") }).strict(),
  z.object({ state: z.literal("pending"), target: z.literal("server") }).strict(),
  z.object({ state: z.literal("saved"), target: z.literal("server"), artistId: recordId, reviewId: recordId,
    scope: z.literal("review_record_and_persona_rows"), ownership: z.literal("unverified") }).strict(),
  z.object({ state: z.literal("failed"), target: z.literal("server"), phase: z.enum(["usage_check", "intake", "review"]),
    confirmedArtistId: recordId.optional(), partialWritePossible: z.boolean(), warning: z.literal("persistence_not_confirmed") }).strict()
]);
export type RunPersistence = z.infer<typeof RunPersistenceSchema>;

const budget = z.number().finite().nonnegative();
const commonRun = z.object({
  schemaVersion: z.literal(1), purpose: z.literal("release_preparation"),
  references: z.object({ artistRecordId: recordId, runId: recordId, projectId: recordId.nullable(), authority: z.literal("record_references_only") }).strict(),
  review: DarkRoomReviewSchema,
  cost: z.object({ kind: z.literal("estimate"), paidModelCostUsd: z.literal(0), paidInputTokens: z.literal(0), paidOutputTokens: z.literal(0), requestBudgetUsd: budget }).strict(),
  persistence: RunPersistenceSchema,
  warnings: z.array(z.enum(["usage_record_failed", "telemetry_unavailable"])).max(2)
}).strict();
export const AssessmentRunSchema = z.discriminatedUnion("lane", [commonRun.extend({ lane: z.literal("local") }), commonRun.extend({ lane: z.literal("server") })])
  .superRefine((run, context) => {
    if (run.references.runId !== run.review.id || run.references.artistRecordId !== run.review.artistId ||
      run.review.seamlineDraft.artistId !== run.review.artistId || run.review.synthaHandoff.artistId !== run.review.artistId ||
      run.review.seamlineDraft.artistName !== run.review.artistName) context.addIssue({ code: z.ZodIssueCode.custom, message: "Inconsistent run references" });
    if (run.review.assessment?.mode !== "local_heuristic") context.addIssue({ code: z.ZodIssueCode.custom, message: "This free run contract supports local preparation rules only" });
    if ((run.lane === "local") !== (run.persistence.state === "local_only")) context.addIssue({ code: z.ZodIssueCode.custom, message: "Persistence state does not match execution lane" });
  });
export type AssessmentRun = z.infer<typeof AssessmentRunSchema>;

function requireFreeRoute(route: AiRouteDecision): void {
  if (!route.allowed || route.mode !== "mock" || route.provider !== "mock" || route.costLedger.estimatedCostUsd !== 0 ||
    route.costLedger.estimatedInputTokens !== 0 || route.costLedger.estimatedOutputTokens !== 0 || route.costLedger.cacheEligible ||
    !Number.isFinite(route.costLedger.maxCostUsd) || route.costLedger.maxCostUsd < 0) throw new Error("A free local preparation route is required");
}

/** Current free lanes share rules; this does not activate paid providers or authenticate record references. */
export function createPreparationRun(artist: ShadowArtistRecord, text: string, route: AiRouteDecision, options: { lane: "local" | "server"; projectId?: string } = { lane: "local" }): AssessmentRun {
  const consent = requireAnalysisConsent(artist.consent);
  if (consentSensitivity(consent) !== "standard") throw new Error("Complete rights and disclosure acceptance before this review lane");
  requireFreeRoute(route);
  if (text.length > route.costLedger.maxInputCharacters) throw new Error("Preparation text exceeds the supported free input cap");
  const review = runDarkRoomLiteMock({ ...artist, intake: (options.lane === "local" ? LocalArtistIntakeSchema : ArtistIntakeSchema).parse(artist.intake), consent }, text);
  return AssessmentRunSchema.parse({
    schemaVersion: 1, purpose: "release_preparation", lane: options.lane,
    references: { artistRecordId: artist.id, runId: review.id, projectId: options.projectId ?? null, authority: "record_references_only" },
    review, cost: { kind: "estimate", paidModelCostUsd: 0, paidInputTokens: 0, paidOutputTokens: 0, requestBudgetUsd: route.costLedger.maxCostUsd },
    persistence: options.lane === "local" ? { state: "local_only", target: "memory" } : { state: "pending", target: "server" }, warnings: []
  });
}

export function failedRunPersistence(run: AssessmentRun, phase: "usage_check" | "intake" | "review", confirmedArtistId?: string): AssessmentRun {
  return AssessmentRunSchema.parse({ ...run, lane: "server", persistence: { state: "failed", target: "server", phase,
    ...(confirmedArtistId ? { confirmedArtistId } : {}), partialWritePossible: phase !== "usage_check", warning: "persistence_not_confirmed" } });
}

/** An insert response confirms rows only; it cannot establish user identity or whole-workspace restoration. */
export async function persistAssessmentRun(run: AssessmentRun, artist: ShadowArtistRecord, repository: Pick<ShadowRepository, "enabled" | "saveIntake" | "saveReview">, route: AiRouteDecision): Promise<AssessmentRun> {
  const parsed = AssessmentRunSchema.parse(run);
  requireFreeRoute(route);
  if (parsed.cost.requestBudgetUsd !== route.costLedger.maxCostUsd) throw new Error("The run budget does not match its persistence ledger");
  if (parsed.lane !== "server" || parsed.persistence.state !== "pending" || artist.id !== parsed.references.artistRecordId) throw new Error("A matching pending server run is required");
  ArtistIntakeSchema.parse(artist.intake);
  if (consentSensitivity(requireAnalysisConsent(artist.consent)) !== "standard") throw new Error("Server persistence requires rights and disclosure acceptance");
  if (!repository.enabled) return AssessmentRunSchema.parse({ ...parsed, persistence: { state: "unavailable", target: "server", warning: "persistence_disabled" } });
  let artistId: string;
  try {
    const result = await repository.saveIntake(artist);
    if (!result.enabled || result.warning) return failedRunPersistence(parsed, "intake");
    artistId = recordId.parse(result.artistId);
  } catch { return failedRunPersistence(parsed, "intake"); }
  try {
    const review = { ...parsed.review, artistId,
      seamlineDraft: { ...parsed.review.seamlineDraft, artistId }, synthaHandoff: { ...parsed.review.synthaHandoff, artistId } };
    const result = await repository.saveReview(review, route.costLedger);
    if (!result.enabled || result.warning) return failedRunPersistence(parsed, "review", artistId);
    const reviewId = recordId.parse(result.reviewId);
    return AssessmentRunSchema.parse({ ...parsed, persistence: { state: "saved", target: "server", artistId, reviewId,
      scope: "review_record_and_persona_rows", ownership: "unverified" } });
  } catch { return failedRunPersistence(parsed, "review", artistId); }
}

export function addRunWarning(run: AssessmentRun, warning: AssessmentRun["warnings"][number]): AssessmentRun {
  return AssessmentRunSchema.parse({ ...run, warnings: [...new Set([...run.warnings, warning])] });
}

/** Compatibility metadata must never infer success from configured credentials or local stub IDs. */
export function runPersistenceMetadata(run: AssessmentRun) {
  const state = run.persistence;
  if (state.state === "saved") return { enabled: true, state: state.state, artistId: state.artistId, reviewId: state.reviewId, scope: state.scope, ownership: state.ownership };
  return { enabled: false, state: state.state, warning: "warning" in state ? state.warning : "persistence_not_confirmed" };
}
